import ControlPointIcon from '@mui/icons-material/ControlPoint';
import {
    Box,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Tooltip,
} from '@mui/material';
import { useState } from 'react';
import {
    AutocompleteInput,
    CreateBase,
    DateTimeInput,
    Form,
    RecordRepresentation,
    ReferenceInput,
    SaveButton,
    SelectInput,
    TextInput,
    Toolbar,
    required,
    useDataProvider,
    useGetIdentity,
    useNotify,
    useRecordContext,
    useUpdate,
} from 'react-admin';
import { Link } from 'react-router-dom';
import { contactInputText, contactOptionText } from '../misc/ContactOption';
import { DialogCloseButton } from '../misc/DialogCloseButton';

import { leadInputText } from '../misc/LeadOption';

export const AddTask = ({
    reference,
    selectContact,
    display = 'chip',
}: {
    reference: 'suppliercontacts' | 'suppliers';
    selectContact?: boolean;
    display?: 'chip' | 'icon';
}) => {
    const { identity } = useGetIdentity();
    const dataProvider = useDataProvider();
    const [update] = useUpdate();
    const notify = useNotify();
    const contact = useRecordContext();
    const [open, setOpen] = useState(false);
    const handleOpen = () => {
        setOpen(true);
    };

    const handleSuccess = async (data: any) => {
        setOpen(false);
        const contact = await dataProvider.getOne(reference, {
            id: reference == 'suppliercontacts' ? data.id_contato : data.id_lead,
        });
        if (!contact.data) return;

        await update(reference, {
            id: contact.data.id,
            data: { ...contact.data, data_hora: new Date().toISOString() },
            previousData: contact.data,
        });

        notify('Tarefa adicionada');
    };

    if (!identity) return null;

    return (
        <>
            {display === 'icon' ? (
                <Tooltip title="Criar tarefa">
                    <IconButton
                        size="small"
                        sx={{
                            color: 'text.secondary',
                            ml: 'auto',
                        }}
                        component={Link}
                        to={'#'}
                        onClick={handleOpen}
                    >
                        <ControlPointIcon fontSize="inherit" color="primary" />
                    </IconButton>
                </Tooltip>
            ) : (
                <Box mt={2} mb={2}>
                    <Chip
                        icon={<ControlPointIcon />}
                        size="small"
                        variant="outlined"
                        onClick={handleOpen}
                        label="Adicionar tarefa"
                        color="primary"
                    />
                </Box>
            )}

            <CreateBase
                resource="tasks"
                record={{
                    id_contato: reference == 'suppliercontacts' ? contact?.id : null,
                    id_fornec: reference == 'suppliers' ? contact?.id : null,
                    id_vendedor: identity.id,
                }}
                transform={data => {
                    const dueDate = new Date(data.dthr_prevista);
                    //dueDate.setHours(0, 0, 0, 0);
                    data.dthr_prevista = dueDate.toISOString();
                    return {
                        ...data,
                        dthr_prevista: new Date(data.dthr_prevista).toISOString(),
                    };
                }}
                mutationOptions={{ onSuccess: handleSuccess }}
            >
                <Dialog
                    open={open}
                    onClose={() => setOpen(false)}
                    aria-labelledby="form-dialog-title"
                    fullWidth
                    disableRestoreFocus
                    maxWidth="sm"
                >
                    <Form>
                        <DialogCloseButton onClose={() => setOpen(false)} />
                        <DialogTitle id="form-dialog-title">
                            {!selectContact
                                ? 'Criar uma nova tarefa para '
                                : 'Criar uma nova tarefa'}
                            {!selectContact && (
                                <RecordRepresentation
                                    record={contact}
                                    resource={reference}
                                />
                            )}
                        </DialogTitle>
                        <DialogContent>
                            <Stack gap={2}>
                                <TextInput
                                    autoFocus
                                    source="descricao_tarefa"
                                    label="Descrição"
                                    validate={required()}
                                    multiline
                                    sx={{ margin: 0 }}
                                    helperText={false}
                                />
                                {selectContact && (
                                    <ReferenceInput
                                        source={reference == 'suppliercontacts' ? "id_contato" : "id_lead"}
                                        reference={reference}
                                    >
                                        <AutocompleteInput
                                            label={reference == 'suppliercontacts' ? "Contato" : "Lead"}
                                            optionText={reference == 'suppliercontacts' ? contactOptionText : "nome_lead"}
                                            inputText={reference == 'suppliercontacts' ? contactInputText : leadInputText}
                                            helperText={false}
                                            validate={required()}
                                            TextFieldProps={{
                                                margin: 'none',
                                            }}
                                        />
                                    </ReferenceInput>
                                )}

                                <Stack direction="row" spacing={1}>
                                    <DateTimeInput
                                        source="dthr_prevista"
                                        validate={required()}
                                        helperText={false}
                                    />
                                    <ReferenceInput
                                        source="id_tipo"
                                        reference="tasktypes"
                                        filter={{ modulo: 'SCM' }}
                                        sort={{ field: 'desc_tipo_tarefa', order: 'ASC' }}
                                    >
                                        <SelectInput
                                            label="Tipo de tarefa"
                                            optionText={choice => choice.desc_tipo_tarefa}
                                            helperText={false}
                                        />
                                    </ReferenceInput>
                                </Stack>
                            </Stack>
                        </DialogContent>
                        <DialogActions sx={{ p: 0 }}>
                            <Toolbar
                                sx={{
                                    width: '100%',
                                }}
                            >
                                <SaveButton onClick={() => setOpen(false)} />
                            </Toolbar>
                        </DialogActions>
                    </Form>
                </Dialog>
            </CreateBase>
        </>
    );
};
